<template>
	<div class="office-page">
		<div class="office-page__header">
			<div class="office-page__header-main">
				<div>
					<div class="page-kicker">OFFICE AI WORKSPACE</div>
					<h1>办公室</h1>
				</div>
				<div class="office-page__mode-switch" role="tablist" aria-label="办公室视图切换">
					<button
						:class="['office-page__mode-button', { active: officeViewMode === 'traditional' }]"
						@click="setOfficeViewMode('traditional')"
					>
						传统
					</button>
					<button
						:class="['office-page__mode-button', { active: officeViewMode === 'metaverse' }]"
						@click="setOfficeViewMode('metaverse')"
					>
						元空间
					</button>
				</div>
				<button class="office-page__launch-button" @click="openMetaverseFullscreen">
					全屏
				</button>
			</div>
			<div v-if="officeViewMode === 'traditional'" class="office-page__header-side">
				<div class="office-agent-stream" aria-label="数字员工工作信息流">
					<div class="office-agent-stream__viewport">
						<div class="office-agent-stream__track">
							<div
								v-for="(item, index) in agentTickerItems.concat(agentTickerItems)"
								:key="`${item.id}-${index}`"
								class="office-agent-stream__item"
							>
								<div class="office-agent-stream__meta">
									<strong>{{ item.name }}</strong>
									<span>{{ item.dimension }}</span>
								</div>
								<p>{{ item.task }}</p>
							</div>
						</div>
					</div>
					<button class="office-agent-stream__button" @click="showWorkDetailDrawer = true">查看工作细节</button>
				</div>
				<div class="office-agent-grid">
					<div
						v-for="agent in agentStatuses"
						:key="agent.id"
						:class="['office-agent-card', { 'is-working': agent.working }]"
					>
						<div class="office-agent-card__top">
							<div class="office-agent-card__name">{{ agent.name }}</div>
							<div class="office-agent-card__queue">
								<span>排队</span>
								<strong>{{ agent.queue }}</strong>
							</div>
						</div>
							<div class="office-agent-card__bottom">
								<div class="office-agent-card__state">
									<span :class="['office-agent-card__dot', { 'is-working': agent.working }]"></span>
									{{ agent.working ? '工作中' : '待命中' }}
								</div>
								<button @click="startAgentDialog(agent)">对话</button>
							</div>
						</div>
						<button class="office-agent-card office-agent-card--add" @click="handleAddAgent">
							<span class="office-agent-card__plus">+</span>
							<div class="office-agent-card__add-label">新增员工</div>
							<div class="office-agent-card__add-tip">接入数字员工</div>
						</button>
				</div>
			</div>
			<div v-else class="office-page__header-side office-page__header-side--meta">
				<div class="office-page__metaverse-tip">
					<span></span>
					已切换到 3D 数字办公室演示视图
				</div>
			</div>
		</div>

		<div
			v-if="officeViewMode === 'traditional'"
			:class="['office-layout', { 'office-layout--sidebar-collapsed': sidebarCollapsed }]"
		>
			<section class="office-chat">
				<div ref="messagesRef" class="office-chat__messages">
					<div
						v-for="message in messages"
						:key="message.id"
						:class="['office-message', 'office-message--' + message.role]"
					>
						<div class="office-message__avatar">
							{{ message.avatar || (message.role === 'user' ? '我' : 'AI') }}
						</div>
						<div class="office-message__bubble">
							<div class="office-message__head">
								<div class="office-message__name">{{ message.senderName || (message.role === 'user' ? '你' : '银合助手') }}</div>
								<span v-if="message.senderMeta" class="office-message__tag">{{ message.senderMeta }}</span>
							</div>
							<div v-if="message.processLead" class="office-message__lead">{{ message.processLead }}</div>
							<div v-if="message.processSteps && message.processSteps.length" class="office-process">
								<div
									v-for="step in message.processSteps"
									:key="step.phase"
									class="office-process__item"
								>
									<div class="office-process__icon">
										<i :class="step.icon"></i>
									</div>
									<div class="office-process__content">
										<div class="office-process__head">
											<strong>{{ step.phase }}</strong>
											<span>{{ step.label }}</span>
										</div>
										<p>{{ step.detail }}</p>
									</div>
								</div>
							</div>
							<div class="office-message__text">{{ message.text }}</div>
							<div v-if="message.highlights && message.highlights.length" class="office-message__highlights">
								<div
									v-for="(item, index) in message.highlights"
									:key="`${message.id}-highlight-${index}`"
									class="office-message__highlight"
								>
									<span></span>
									<p>{{ item }}</p>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div class="office-composer">
					<div ref="senderRef" class="office-composer__editor"></div>
					<div class="office-composer__actions">
						<div class="office-composer__tips">
							<span>@ 可提及成员</span>
							<span># 可插入命令</span>
							<span>/ 可选模板</span>
						</div>
						<Button color="primary" :loading="replying" @click="sendCurrent">发送消息</Button>
					</div>
				</div>
			</section>

			<aside :class="['office-sidebar', { 'office-sidebar--collapsed': sidebarCollapsed }]">
				<div class="office-sidebar__header">
					<button class="office-sidebar__toggle" @click="toggleSidebar">
						<i :class="sidebarCollapsed ? 'fa fa-angle-left' : 'fa fa-angle-right'"></i>
					</button>
					<div class="office-sidebar__tabs">
						<button
							v-for="tab in sidebarTabs"
							:key="tab.key"
							:class="['office-sidebar__tab', { active: activeSidebarTab === tab.key }]"
							@click="activeSidebarTab = tab.key"
						>
							<i :class="tab.icon"></i>
							<span>{{ tab.label }}</span>
						</button>
					</div>
				</div>

				<div v-if="!sidebarCollapsed" class="office-sidebar__content">
					<div class="office-template-list" id="templates">
						<button
							v-for="item in activeSidebarItems"
							:key="item.title"
							class="office-template"
							@click="handleSidebarItem(item)"
						>
							<div class="office-template__head">
								<b>{{ item.title }}</b>
								<span v-if="item.status" :class="['office-template__badge', `is-${item.statusType || 'neutral'}`]">{{ item.status }}</span>
							</div>
							<template v-if="activeSidebarTab === 'todo'">
								<div class="office-template__info-grid">
									<div class="office-template__info-item">
										<label>发送员工</label>
										<div>{{ item.agent }}</div>
									</div>
									<div class="office-template__info-item">
										<label>触发时间</label>
										<div>{{ item.time }}</div>
									</div>
								</div>
								<div class="office-template__body">
									<label>事项内容</label>
									<p>{{ item.preview }}</p>
								</div>
								<div class="office-template__footer">
									<span class="office-template__handle-label">是否处理</span>
									<strong>{{ item.handled }}</strong>
								</div>
							</template>
							<template v-else-if="activeSidebarTab === 'files'">
								<div class="office-template__info-grid">
									<div class="office-template__info-item">
										<label>发送员工</label>
										<div>{{ item.agent }}</div>
									</div>
									<div class="office-template__info-item">
										<label>发送时间</label>
										<div>{{ item.time }}</div>
									</div>
								</div>
								<div class="office-template__body">
									<label>文件内容</label>
									<p>{{ item.preview }}</p>
								</div>
								<div class="office-template__footer">
									<span class="office-template__handle-label">文件状态</span>
									<strong>{{ item.handled }}</strong>
								</div>
							</template>
							<template v-else>
								<span>{{ item.preview }}</span>
							</template>
						</button>
					</div>
				</div>
			</aside>
		</div>
		<div v-else class="office-metaverse">
			<iframe
				class="office-metaverse__frame"
				:src="metaverseOfficeUrl"
				title="元空间数字办公室"
				frameborder="0"
				allow="fullscreen"
			></iframe>
		</div>
		<Modal v-model="showWorkDetailDrawer" type="drawer-right" hasCloseIcon>
			<div slot="header">工作细节</div>
			<div class="office-work-drawer">
				<div
					v-for="item in agentWorkDetails"
					:key="item.id"
					class="office-work-drawer__item"
				>
					<div class="office-work-drawer__head">
						<div>
							<strong>{{ item.name }}</strong>
							<span>{{ item.dimension }}</span>
						</div>
						<em>{{ item.time }}</em>
					</div>
					<p>{{ item.task }}</p>
					<div class="office-work-drawer__footer">
						<label>下一步</label>
						<div>{{ item.next }}</div>
					</div>
				</div>
			</div>
		</Modal>
	</div>
</template>

<script>
import XSender from 'x-sender/lib/XSender.mjs';
import 'x-sender/lib/XSender.css';

function buildTaxForecastMessage() {
	return {
		role: 'assistant',
		senderName: '财务数字员工',
		senderMeta: '税务测算',
		avatar: '财',
		processLead: '',
		processSteps: [
			{
				icon: 'fa fa-sitemap',
				phase: '规划模型',
				label: '思考',
				detail: '按年度税负预测口径完成拆解，先确认税种范围，再调取三表与本年申报口径进行校准。将问题拆分为增值税及附加、企业所得税两条主线，并采用“本年累计数据 + 剩余月份滚动年化”进行预测。'
			},
			{
				icon: 'fa fa-table',
				phase: '数据调用',
				label: '查看财务三表',
				detail: '已读取利润表、资产负债表、现金流量表，同时交叉核对收入确认、成本归集、进项税额和历史申报记录。'
			},
			{
				icon: 'fa fa-line-chart',
				phase: '数据分析',
				label: '税负测算',
				detail: '按当前经营节奏估算，全年营业收入与利润将保持平稳增长；剔除一次性费用后，应纳税所得额预计落在 350 万至 380 万区间。'
			}
		],
		text: '预计今年需缴纳税费合计约 126 万至 138 万元，其中企业所得税约 87 万至 95 万元，增值税及附加约 39 万至 43 万元。',
		highlights: [
			'若二季度可确认更多研发费用加计扣除，企业所得税还有约 6 万至 9 万元下调空间。',
			'建议在 6 月和 9 月各做一次滚动复核，重点关注收入确认节奏和大额费用入账时点。'
		]
	};
}

export default {
	name: 'Office',
	data() {
		const routeView = this.$route && this.$route.query ? this.$route.query.view : '';
		return {
			sender: null,
			replying: false,
			sidebarCollapsed: false,
			showWorkDetailDrawer: false,
			officeViewMode: routeView === 'traditional' ? 'traditional' : 'metaverse',
			metaverseOfficeUrl: `${window.location.origin}/office-api`,
			activeSidebarTab: 'todo',
			sidebarTabs: [
				{ key: 'quick', label: '快捷', icon: 'fa fa-bolt' },
				{ key: 'todo', label: '代办', icon: 'fa fa-check-square-o' },
				{ key: 'files', label: '文件', icon: 'fa fa-file-text-o' }
			],
			agentStatuses: [
				{ id: 'finance-agent', name: '财务数字员工', queue: 3, working: true, summary: '正在核对回款异常与现金流。' },
				{ id: 'hr-agent', name: 'HR数字员工', queue: 2, working: true, summary: '正在推进 Offer 与面试反馈。' },
				{ id: 'legal-agent', name: '法务数字员工', queue: 1, working: false, summary: '当前等待合同条款确认。' },
				{ id: 'data-agent', name: '数据专员', queue: 4, working: true, summary: '正在整理周报和指标文件。' }
			],
			agentWorkDetails: [
				{
					id: 'finance-cashflow',
					name: '财务数字员工',
					dimension: '资金监控',
					time: '2 分钟前',
					task: '正在复核回款异常客户的账龄变化、现金流缺口和本周应付排期，准备输出最新税负与现金调度影响。',
					next: '生成资金排期建议，并同步提醒本周需优先确认的回款客户。'
				},
				{
					id: 'hr-offer',
					name: 'HR数字员工',
					dimension: '招聘推进',
					time: '7 分钟前',
					task: '已汇总候选人面试反馈、薪酬区间和入职时间窗口，正在等待业务负责人确认最终 Offer 版本。',
					next: '输出 Offer 沟通要点，并形成需要线下推进的确认清单。'
				},
				{
					id: 'legal-contract',
					name: '法务数字员工',
					dimension: '合同审核',
					time: '11 分钟前',
					task: '正在比对供应商合同补充条款与标准模板差异，重点标记数据使用、违约责任与付款节点风险。',
					next: '生成风险条款摘要，并给采购同事一版可直接转发的修改建议。'
				},
				{
					id: 'data-weekly',
					name: '数据专员',
					dimension: '经营分析',
					time: '15 分钟前',
					task: '正在汇总收入、成本、现金流和重点波动原因，准备更新经营周报和老板简报底稿。',
					next: '输出最新经营摘要，并同步更新本周关键指标文件。'
				}
			],
			messages: [
				{
					id: 1,
					role: 'user',
					senderName: '你',
					senderMeta: '办公室提问',
					avatar: '我',
					text: '预计今年要交多少税？'
				},
				Object.assign({ id: 2 }, buildTaxForecastMessage())
			],
			prompts: [
				{ title: '老板简报', preview: '汇总本周经营情况、税务风险和重点动作。', text: '请根据本周的经营、报税和现金流情况，整理一份老板简报。' },
				{ title: '会议纪要', preview: '把讨论结果整理成清晰的待办列表。', text: '请把今天办公室讨论内容整理成会议纪要，并拆分为待办清单。' },
				{ title: '税务待办', preview: '列出本月需要处理的税务工作。', text: '请按优先级列出本月办公室需要推进的税务待办事项。' }
			],
			todoItems: [
				{
					title: '回款异常待确认',
					agent: '财务数字员工',
					time: '今天 09:40',
					preview: '华东区两笔应收账款超过约定回款日 7 天，建议先确认客户承诺回款时间，再决定是否升级催收。',
					handled: '待用户确认',
					status: '待确认',
					statusType: 'warning',
					text: '请根据华东区两笔超期应收，生成一份需要老板确认的回款跟进建议。'
				},
				{
					title: 'Offer 线下沟通提醒',
					agent: 'HR数字员工',
					time: '今天 11:15',
					preview: '候选人王宁已通过终面，但薪资区间需要线下和业务负责人再次确认后才能发出 Offer。',
					handled: '处理中',
					status: '线下推进',
					statusType: 'info',
					text: '请整理候选人王宁的沟通要点，并生成一份给业务负责人的确认消息。'
				},
				{
					title: '合同补充条款待决定',
					agent: '法务数字员工',
					time: '今天 14:20',
					preview: '供应商框架合同中新增了数据使用补充条款，需要你决定是否接受现版本，或要求对方回退到标准条款。',
					handled: '未处理',
					status: '待处理',
					statusType: 'neutral',
					text: '请总结供应商合同补充条款的风险点，并给出可回复对方的修改意见。'
				}
			],
			fileItems: [
				{
					title: '经营周报-整理版.xlsx',
					agent: '数据专员',
					time: '今天 08:55',
					preview: '已整理成可直接发送的经营周报文件，包含收入、成本、现金流和异常波动摘要。',
					handled: '待查看',
					status: '新文件',
					statusType: 'success',
					text: '请基于经营周报-整理版.xlsx，补一段发给老板的摘要说明。'
				},
				{
					title: '4月招聘推进纪要.docx',
					agent: 'HR数字员工',
					time: '今天 12:30',
					preview: '已把本周招聘推进会内容整理成文档，包含候选人状态、卡点和建议动作。',
					handled: '已接收',
					status: '已送达',
					statusType: 'info',
					text: '请根据 4月招聘推进纪要.docx，整理出下周需要继续推进的招聘动作。'
				},
				{
					title: '合同风险对照清单.pdf',
					agent: '法务数字员工',
					time: '今天 15:10',
					preview: '已输出合同风险对照文件，标注了需回退条款、可接受条款和待法务复核的部分。',
					handled: '待确认',
					status: '待确认',
					statusType: 'warning',
					text: '请根据 合同风险对照清单.pdf，生成一段给采购同事的确认建议。'
				}
			]
		};
	},
	computed: {
		agentTickerItems() {
			return this.agentWorkDetails.map(item => ({
				id: item.id,
				name: item.name,
				dimension: item.dimension,
				task: item.task
			}));
		},
		activeSidebarTitle() {
			const current = this.sidebarTabs.find(item => item.key === this.activeSidebarTab);
			return current ? current.label : '快捷';
		},
		activeSidebarItems() {
			if (this.activeSidebarTab === 'todo') {
				return this.todoItems;
			}
			if (this.activeSidebarTab === 'files') {
				return this.fileItems;
			}
			return this.prompts;
		}
	},
	methods: {
		setOfficeViewMode(mode) {
			if (this.officeViewMode === mode) {
				return;
			}
			this.officeViewMode = mode;
			this.$router.replace({
				path: this.$route.path,
				query: mode === 'traditional' ? { view: 'traditional' } : {}
			});
		},
		openMetaverseFullscreen() {
			window.location.href = this.metaverseOfficeUrl;
		},
		ensureTraditionalWorkspaceReady() {
			this.$nextTick(() => {
				if (this.officeViewMode !== 'traditional') {
					return;
				}
				if (!this.sender && this.$refs.senderRef) {
					this.initSender();
					return;
				}
				if (this.sender) {
					this.sender.focus();
				}
				this.scrollToLatest();
			});
		},
		teardownSender() {
			if (this.sender) {
				this.sender.destroy();
				this.sender = null;
			}
		},
		initSender() {
			if (!this.$refs.senderRef || this.sender) {
				return;
			}
			this.sender = new XSender(this.$refs.senderRef, {
				placeholder: '输入消息，按 Enter 发送，Shift + Enter 换行',
				mentionConfig: {
					dialogTitle: '提及成员',
					options: [
						{ id: 'finance-agent', name: '财务数字员工' },
						{ id: 'hr-agent', name: 'HR数字员工' },
						{ id: 'legal-agent', name: '法务数字员工' },
						{ id: 'data-agent', name: '数据专员' },
						{ id: 'u-1', name: '财税经理 李青' },
						{ id: 'u-2', name: '运营助理 苏宁' },
						{ id: 'u-3', name: '老板办公室' }
					]
				},
				triggerConfig: [
					{
						dialogTitle: '选择命令',
						key: '#',
						options: [
							{ id: 'summary', name: '生成总结' },
							{ id: 'todo', name: '提炼待办' },
							{ id: 'tax', name: '财税分析' }
						]
					}
				],
				selectConfig: [
					{
						dialogTitle: '插入模板',
						key: '/',
						options: [
							{ id: 'brief', name: '老板简报' },
							{ id: 'meeting', name: '会议纪要' },
							{ id: 'report', name: '税务周报' }
						]
					}
				]
			});
			this.sender.bus.on('office-page', XSender.EventSet.EVENT_COMMON_SEND, () => {
				this.sendCurrent();
			});
			this.sender.focus();
			this.$nextTick(() => {
				this.scrollToLatest();
			});
		},
		toggleSidebar() {
			this.sidebarCollapsed = !this.sidebarCollapsed;
		},
		startAgentDialog(agent) {
			const _this = this;
			if (!_this.sender || !agent) {
				return;
			}
			_this.sender.focus();
			_this.sender.setMention({
				id: agent.id,
				name: agent.name
			}).then(function () {
				return _this.sender.setText(' ');
			});
		},
		applyPrompt(prompt) {
			if (!this.sender) {
				return;
			}
			this.sender.setText(prompt.text);
			this.sender.focus();
		},
		handleSidebarItem(item) {
			this.applyPrompt(item);
		},
		handleAddAgent() {
			this.applyPrompt({
				text: '请新增一个数字员工，并配置岗位职责、触发条件、协作对象和可交付内容。'
			});
		},
		sendCurrent() {
			if (!this.sender || this.replying) {
				return;
			}
			const text = this.sender.getText().trim();
			if (!text) {
				return;
			}
			this.messages.push({
				id: Date.now(),
				role: 'user',
				senderName: '你',
				senderMeta: '办公室提问',
				avatar: '我',
				text
			});
			this.replying = true;
			this.sender.reset();
			window.setTimeout(() => {
				const reply = this.buildAssistantMessage(text);
				reply.id = Date.now() + 1;
				this.messages.push(reply);
				this.replying = false;
				this.$nextTick(() => {
					this.scrollToLatest();
				});
			}, 560);
			this.$nextTick(() => {
				this.scrollToLatest();
			});
		},
		buildAssistantMessage(text) {
			if (text.indexOf('简报') > -1) {
				return {
					role: 'assistant',
					senderName: '数据专员',
					senderMeta: '经营汇总',
					avatar: '数',
					text: '已按老板简报口径完成整理，建议采用“经营结果、风险提示、下周动作”三段式输出，方便直接转发管理层。'
				};
			}
			if (text.indexOf('会议') > -1) {
				return {
					role: 'assistant',
					senderName: 'HR数字员工',
					senderMeta: '纪要整理',
					avatar: 'HR',
					text: '建议输出结构为结论摘要、责任人、截止时间和阻塞项，整理后可直接同步到办公室代办。'
				};
			}
			if (text.indexOf('税') > -1) {
				return buildTaxForecastMessage();
			}
			if (text.indexOf('合同') > -1) {
				return {
					role: 'assistant',
					senderName: '法务数字员工',
					senderMeta: '合同审阅',
					avatar: '法',
					text: '我会先提取合同关键条款、比对标准模板差异，再输出风险等级、修改建议和对外回复版本。'
				};
			}
			return {
				role: 'assistant',
				senderName: '数据专员',
				senderMeta: '任务响应',
				avatar: '数',
				text: '已接收需求。建议先明确目标、调取对应数据，再输出可直接执行的结论和后续动作。'
			};
		},
		scrollToLatest() {
			if (!this.$refs.messagesRef) {
				return;
			}
			this.$refs.messagesRef.scrollTop = this.$refs.messagesRef.scrollHeight;
		}
	},
	watch: {
		officeViewMode(value) {
			if (value === 'traditional') {
				this.ensureTraditionalWorkspaceReady();
				return;
			}
			this.teardownSender();
		},
		'$route.query.view'(value) {
			this.officeViewMode = value === 'traditional' ? 'traditional' : 'metaverse';
		}
	},
	mounted() {
		if (this.officeViewMode === 'traditional') {
			this.ensureTraditionalWorkspaceReady();
		}
	},
	beforeDestroy() {
		this.teardownSender();
	}
};
</script>
